import { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import partialRight from 'lodash/partialRight';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiData } from '@wix/da-bi/pkg/events';
import { LoadableComponentContext } from '@wix/da-hooks/pkg/useLoadableComponent';
import { useEditorDraftUnmountEffects, useEditorDraftState } from '@wix/da-editor-tiptap/pkg/clientDrafts/hooks';
import { EditorContext } from '@wix/da-editor-tiptap/pkg/components/Editor/Context';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { getPublishResult, isBusy as isBusySelector } from '../redux/selectors';
import { CommentEditorSource } from '../redux/types';
import { usePublishSuccessCleanup } from './usePublishSuccessCleanup';
import { useForceLoadIfNeeded } from './useForceLoadIfNeeded';
import { useShouldFocus } from './useShouldFocus';
import { focusDone } from '../redux/actionCreators';
/** Provides all the state and effects you'll need for a Comment editor! */ export function useCommentEditorSpecifics(commentBoxId, commentableItemId, commentableType, { editorSource, relevantComment, initiallyExpanded, onPublishSuccess } = {
    editorSource: CommentEditorSource.Independent
}) {
    const shouldFocus = useShouldFocus(commentBoxId);
    const logBiEvent = useBiLogger();
    const { triggerLoad } = useContext(LoadableComponentContext);
    const [isExpanded, setIsExpanded] = useState(initiallyExpanded || shouldFocus || editorSource === CommentEditorSource.Edit);
    // Handle everything for expanding a comment box:
    //  - Logging BI
    //  - Updating the loading context so that our CommentEditor chunk gets loaded
    //  - Updating the local state's isExpanded indicator
    const expandEditor = useCallback(()=>{
        if (!isExpanded) {
            logBiEvent(BiData({
                evid: 183,
                itemid: typeof commentableItemId === 'string' ? parseInt(commentableItemId) : commentableItemId,
                typeid: commentableType
            }));
            triggerLoad();
            setIsExpanded(true);
        }
    }, [
        logBiEvent,
        commentableItemId,
        commentableType,
        triggerLoad,
        isExpanded
    ]);
    const collapseEditor = useCallback(()=>setIsExpanded(false), []);
    const editorDraftState = useEditorDraftState(commentBoxId);
    // We need to figure out what the content of the comment box should be on mount. There are two options:
    //  1. A draft that the user started, but left the page
    //  2. The content of an existing comment that the user is editing.
    let initialContent = null;
    if (editorDraftState) {
        initialContent = editorDraftState;
    } else if (editorSource === CommentEditorSource.Edit && relevantComment) {
        initialContent = relevantComment.textContent;
    }
    useCommentEditorEffects({
        editorId: commentBoxId,
        shouldFocus,
        expandEditor,
        collapseEditor,
        onPublishSuccess,
        editorSource,
        hasInitialContent: !!initialContent
    });
    // Now some values from redux we pass straight through
    const isBusy = useSelector(partialRight(isBusySelector, commentBoxId));
    return {
        editorSource,
        isBusy,
        relevantComment,
        shouldFocus,
        isExpanded,
        expandEditor,
        collapseEditor,
        initialContent
    };
}
/**
 * Handles all the effects that a comment editor needs to handle.
 *
 * Some of what it handles:
 *  - Draft saving/deleting
 *  - Error handling
 *  - Expand on focus
 *  - Collapse on publish
 *  - and more!
 */ function useCommentEditorEffects({ editorId, shouldFocus, collapseEditor, expandEditor, onPublishSuccess, editorSource, hasInitialContent }) {
    const { ref: editor, isReady: isEditorReady } = useContext(EditorContext);
    useEditorDraftUnmountEffects(editorId);
    const publishResult = useSelector(partialRight(getPublishResult, editorId));
    // Make sure we trigger onPublishSuccess if publishResult comes back successful
    // TODO: Run this in a saga somewhere instead?
    usePublishSuccessCleanup(editorId, publishResult, onPublishSuccess);
    // Collapse the editor after the user successfully publishes a comment.
    useEffect(()=>{
        if (publishResult) {
            collapseEditor();
        }
    }, [
        publishResult,
        collapseEditor
    ]);
    // Expand the editor if it's ever focused.
    useEffect(()=>{
        if (editor) {
            const onFocus = ()=>expandEditor();
            editor.on('focus', onFocus);
            return ()=>{
                editor.off('focus', onFocus);
            };
        }
    }, [
        editor,
        expandEditor
    ]);
    // Focus the editor if requested and clear out the request.
    const dispatchFocusDone = useAction(focusDone);
    useEffect(()=>{
        if (shouldFocus && editor && isEditorReady) {
            const onFocus = ()=>{
                dispatchFocusDone(editorId);
            };
            editor.on('focus', onFocus);
            editor.commands.focus();
            return ()=>{
                editor.off('focus', onFocus);
            };
        }
    }, [
        editorId,
        expandEditor,
        shouldFocus,
        isEditorReady,
        editor,
        dispatchFocusDone
    ]);
    useForceLoadIfNeeded(editorSource, shouldFocus, hasInitialContent);
}

//# sourceMappingURL=useCommentEditorSpecifics.js.map